import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Form, Input, Button, Message, Alert, Tag } from '@arco-design/web-react';
import { IconLock, IconEmail, IconCheckCircle } from '@arco-design/web-react/icon';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/services/apiClient';
import { API_ENDPOINTS } from '@/config/apiEndpoints';
import { useTranslation } from 'react-i18next';
import PublicLayout from './PublicLayout';
import { formatRoleLabel } from '@/modules/common/helpers/roleLabels';

const FormItem = Form.Item;

function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const { t } = useTranslation('public');
  const directBaseUrl = typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'http://localhost:8010'
    : null;

  // Derivar token de URL params
  const invitationToken = searchParams.get('invitation_token');

  // React Query para validar invitación
  const { data: invitationData = null } = useQuery({
    queryKey: ['invitation', 'validate', invitationToken],
    queryFn: async () => {
      const response = await apiClient.get(API_ENDPOINTS.PUBLIC_INVITE_VALIDATE, { token: invitationToken });
      const data = response?.data;
      if (data?.valid) return data.invitation;
      return null;
    },
    enabled: !!invitationToken,
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });

  // Sincronizar email de invitación al formulario
  useEffect(() => {
    if (invitationData?.email) {
      form.setFieldValue('email', invitationData.email);
    }
  }, [invitationData, form]);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    setSubmitError(null);

    try {
      const payload = {
        email: values.email,
        password: values.password
      };

      // Add invitation token if present
      if (invitationToken) {
        payload.invitation_token = invitationToken;
      }

      let response;
      try {
        response = await apiClient.post(API_ENDPOINTS.PUBLIC_REGISTER, {}, payload);
      } catch (err) {
        const isTimeout = err?.code === 'ECONNABORTED';
        if (isTimeout && directBaseUrl) {
          response = await apiClient.post(
            API_ENDPOINTS.PUBLIC_REGISTER,
            {},
            payload,
            { baseURL: directBaseUrl }
          );
        } else {
          throw err;
        }
      }
      const data = response?.data;

      if (data?.success) {
        Message.success(t('auth:register.messages.accountCreated'));

        // Redirigir a login
        setTimeout(() => {
          navigate('/login');
        }, 1500);
      } else {
        const message = data?.message || t('auth:errors.couldNotCreateAccount');
        setSubmitError(message);
        Message.error(message);
      }
    } catch (err) {
      console.error('Error registering:', err);

      if (err.response?.status === 409) {
        const message = t('auth:errors.emailAlreadyRegistered');
        setSubmitError(message);
        Message.error(message);
      } else if (err.response?.status === 422) {
        // Error de validación - mostrar detalles
        const detail = err.response?.data?.detail;

        if (Array.isArray(detail)) {
          // Pydantic validation errors
          const errors = detail.map(e => {
            const field = e.loc?.join('.') || 'campo';
            return `${field}: ${e.msg}`;
          }).join('\n');
          const message = `${t('auth:errors.validationError')}:\n${errors}`;
          setSubmitError(message);
          Message.error(message);
          console.error('Validation errors:', detail);
        } else if (typeof detail === 'string') {
          setSubmitError(detail);
          Message.error(detail);
        } else {
          const message = t('auth:errors.validationCheckData');
          setSubmitError(message);
          Message.error(message);
        }
      } else {
        const isTimeout = err?.code === 'ECONNABORTED';
        const errorMessage = isTimeout
          ? t('auth:errors.serverTimeout')
          : (err.response?.data?.detail || t('auth:errors.couldNotCreateAccount'));
        setSubmitError(errorMessage);
        Message.error(errorMessage);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicLayout>
      <div className="public-card">
        <div className="public-card-header">
          <h1>{t('registration.createAccount')}</h1>
          <p>{t('registration.registerSubtitle')}</p>
        </div>

        {invitationData ? (
          <Alert
            type="success"
            icon={<IconCheckCircle />}
            content={
              <div>
                <strong>{t('registration.invitationValid')}</strong>
                <div style={{ marginTop: 8, fontSize: 13 }}>
                  Has sido invitado como: <Tag color="blue">{formatRoleLabel(invitationData.role?.name) || t('registration.user')}</Tag>
                </div>
              </div>
            }
            style={{ marginBottom: 24 }}
          />
        ) : (
          <Alert
            type="info"
            content={t('registration.useInvitationLink')}
            style={{ marginBottom: 24 }}
            closable
          />
        )}

        <Form
          form={form}
          layout="vertical"
          onSubmit={handleSubmit}
          autoComplete="off"
        >
          {submitError && (
            <Alert
              type="error"
              content={submitError}
              style={{ marginBottom: 16 }}
            />
          )}
          <FormItem
            label={t('registration.email')}
            field="email"
            rules={[
              { required: true, message: t('registration.emailRequired') },
              { type: 'email', message: t('registration.emailInvalid') }
            ]}
          >
            <Input
              prefix={<IconEmail />}
              placeholder={t('registration.emailPlaceholder')}
              size="large"
            />
          </FormItem>

          <FormItem
            label={t('registration.password')}
            field="password"
            rules={[
              { required: true, message: t('registration.passwordRequired') },
              { minLength: 8, message: t('registration.passwordMin') },
              {
                validator: (value, callback) => {
                  if (!value) {
                    callback();
                    return;
                  }

                  // Validaciones que coinciden con el backend
                  const hasUppercase = /[A-Z]/.test(value);
                  const hasLowercase = /[a-z]/.test(value);
                  const hasNumber = /[0-9]/.test(value);

                  if (!hasUppercase) {
                    callback(t('registration.passwordUppercase'));
                  } else if (!hasLowercase) {
                    callback(t('registration.passwordLowercase'));
                  } else if (!hasNumber) {
                    callback(t('registration.passwordNumber'));
                  } else {
                    callback();
                  }
                }
              }
            ]}
            extra={
              <div style={{ fontSize: 12, color: 'var(--color-text-3)', marginTop: 4 }}>
                {t('registration.passwordRequirements')}
                <ul style={{ margin: '4px 0 0 20px', padding: 0 }}>
                  <li>{t('registration.passwordReq1')}</li>
                  <li>{t('registration.passwordReq2')}</li>
                  <li>{t('registration.passwordReq3')}</li>
                  <li>{t('registration.passwordReq4')}</li>
                </ul>
              </div>
            }
          >
            <Input.Password
              prefix={<IconLock />}
              placeholder={t('registration.passwordExamplePlaceholder')}
              size="large"
            />
          </FormItem>

          <FormItem
            label={t('registration.confirmPassword')}
            field="confirmPassword"
            rules={[
              { required: true, message: t('registration.confirmPasswordRequired') },
              {
                validator: (value, callback) => {
                  const password = form.getFieldValue('password');
                  if (value && value !== password) {
                    callback(t('registration.passwordsMismatch'));
                  } else {
                    callback();
                  }
                }
              }
            ]}
          >
            <Input.Password
              prefix={<IconLock />}
              placeholder={t('registration.confirmPasswordPlaceholder')}
              size="large"
            />
          </FormItem>

          <FormItem style={{ marginBottom: 0, marginTop: 32 }}>
            <Button
              type="primary"
              htmlType="submit"
              long
              size="large"
              loading={submitting}
            >
              {t('registration.createAccount')}
            </Button>
          </FormItem>
        </Form>

        <div style={{ marginTop: 24, textAlign: 'center', color: '#86909c', fontSize: 14 }}>
          {t('registration.alreadyHaveAccount')}{' '}
          <a href="/login" style={{ color: '#667eea', fontWeight: 500 }}>
            {t('registration.signIn')}
          </a>
        </div>

        <div style={{ marginTop: 16, textAlign: 'center', color: '#86909c', fontSize: 14 }}>
          {t('registration.haveInvitation')}{' '}
          <a href="#" style={{ color: '#667eea', fontWeight: 500 }}>
            {t('registration.useItHere')}
          </a>
        </div>
      </div>
    </PublicLayout>
  );
}

export default RegisterPage;
